import { useEffect, useRef, useState } from "react";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import { aiNav, aiNavAnchors } from "@/mocks/aiassist";
import { aiPrompts, promptIcons, promptKinds, promptsHint, sampleDraft, sampleDraftEn, type AiPrompt } from "@/mocks/aiprompts";
import { goAnchor } from "./app-nav";
import { t } from "@/lib/i18n";

/* Живой макет «AI Prompts»: правила, которые правят черновик ответа продавца.
   Всё работает в макете — переключатели, создание и правка своих промптов, применение к превью;
   настоящие ответы клиентам не меняются. */

const INPUT =
  "w-full box-border min-w-0 rounded-btn border border-border bg-background px-3 text-[13px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25";

const GRAMMAR: [RegExp, string][] = [
  [/здраствуйте/gi, "Здравствуйте"],
  [/подскажыте/gi, "Подскажите"],
  [/пажалуста/gi, "пожалуйста"],
  [/можна/gi, "можно"],
  [/\?{2,}/g, "?"],
];

const applyGrammar = (text: string) => GRAMMAR.reduce((acc, [re, to]) => acc.replace(re, to), text);

function IconChoice({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {promptIcons.map((icon) => (
        <button
          key={icon}
          type="button"
          onClick={() => onChange(icon)}
          aria-label={t("Иконка {icon}", { icon })}
          aria-pressed={value === icon}
          className={cn(
            "grid size-8 cursor-pointer place-items-center rounded-btn border text-[15px] leading-none transition",
            value === icon ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground",
          )}
        >
          <i className={icon} aria-hidden />
        </button>
      ))}
    </div>
  );
}

function PromptsDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const [items, setItems] = useState<AiPrompt[]>(aiPrompts);
  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", description: "", instruction: "", icon: promptIcons[4], kind: "tone" });
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(sampleDraft);
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4200);
  };

  const onNav = (id: string) => {
    if (id === "ai-prompts") return;
    const anchor = aiNavAnchors[id];
    if (anchor) {
      goAnchor(anchor);
      return;
    }
    flash(t("Раздел «{name}» в демо не открывается", { name: aiNav.flatMap((g) => g.items).find((x) => x.id === id)?.label ?? "" }));
  };

  const toggle = (id: string) => {
    setItems((prev) => prev.map((p) => (p.id === id ? { ...p, enabled: !p.enabled } : p)));
  };

  const openAdd = () => {
    setEditId(null);
    setForm({ name: "", description: "", instruction: "", icon: promptIcons[4], kind: "tone" });
    setError("");
    setAddOpen(true);
  };

  const openEdit = (p: AiPrompt) => {
    setEditId(p.id);
    setForm({ name: p.title, description: p.description, instruction: p.instruction, icon: p.icon, kind: "custom" });
    setError("");
    setAddOpen(true);
  };

  const save = () => {
    const name = form.name.trim();
    const description = form.description.trim();
    const instruction = form.instruction.trim();
    if (name.length < 3) {
      setError(t("Название — минимум 3 символа: по нему промпт выбирают в диалоге"));
      return;
    }
    const clash = items.some((p) => p.id !== editId && p.title.toLowerCase() === name.toLowerCase());
    if (clash) {
      setError(t("Промпт с таким названием уже есть — придумайте другое"));
      return;
    }
    if (description.length < 10) {
      setError(t("Опишите в одном предложении, что делает промпт — минимум 10 символов"));
      return;
    }
    if (instruction.length < 20) {
      setError(t("Инструкция для ассистента — минимум 20 символов, иначе правило не сработает"));
      return;
    }

    if (editId) {
      setItems((prev) => prev.map((p) => (p.id === editId ? { ...p, title: name, description, instruction, icon: form.icon } : p)));
      flash(t("Промпт «{name}» сохранён — новое правило применится к следующим черновикам", { name }));
    } else {
      setItems((prev) => [
        ...prev,
        { id: `p-${Date.now()}`, title: name, description, instruction, icon: form.icon, enabled: true, builtin: false },
      ]);
      flash(t("Промпт «{name}» создан и включён — он появится в панели AI Prompts в диалоге", { name }));
    }
    setAddOpen(false);
    setEditId(null);
  };

  const remove = (p: AiPrompt) => {
    setItems((prev) => prev.filter((x) => x.id !== p.id));
    flash(t("Промпт «{name}» удалён — ассистент больше не будет применять это правило", { name: p.title }));
  };

  const enabled = items.filter((p) => p.enabled);
  const has = (id: string) => enabled.some((p) => p.id === id);

  const run = () => {
    if (enabled.length === 0) {
      flash(t("Все AI Prompts выключены — включите хотя бы один, иначе править черновик нечем"));
      return;
    }
    let out = draft;
    if (has("p-grammar")) out = applyGrammar(out);
    if (has("p-simplify")) out = out.replace(/, и можно ли оплатить по счёту/i, ". Можно оплатить по счёту?");
    if (has("p-tone")) out = out.replace(/^Здравствуйте!/, "Добрый день!").replace(/\s+$/, "");
    if (has("p-translate")) out = sampleDraftEn;
    setDraft(out);
    flash(t("Применили: {list}", { list: enabled.map((p) => p.title).join(", ") }));
  };

  const canSave = form.name.trim().length >= 3 && form.description.trim().length >= 10 && form.instruction.trim().length >= 20;

  return (
    <Band id="ai-prompts" tone="default">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} / {t("настройки → входящие → AI Prompts")}
          </span>
          <span className="ms-auto inline-flex items-center gap-2 whitespace-nowrap text-[11px] text-muted-foreground">
            <span className="size-1.5 rounded-full bg-primary" aria-hidden />
            {t("Включено {n} из {total}", { n: enabled.length, total: items.length })}
          </span>
        </div>

        <div className="grid lg:grid-cols-[14.5rem_minmax(0,1fr)]">
          {/* ── настройки ── */}
          <div className="hidden flex-col gap-4 border-border p-3 lg:flex lg:border-e">
            {aiNav.map((g) => (
              <div key={g.group} className="flex flex-col gap-0.5">
                <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{g.group}</p>
                {g.items.map((it) => {
                  const active = it.id === "ai-prompts";
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onNav(it.id)}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "flex cursor-pointer items-center gap-2.5 rounded-btn px-2.5 py-2 text-start text-[12.5px] font-medium transition",
                        active ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                    >
                      <i className={cn(it.icon, "text-base leading-none")} aria-hidden />
                      <span className="min-w-0 flex-1 truncate">{it.label}</span>
                      {it.badge ? (
                        <span className="rounded-full bg-amber-400/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-600">{it.badge}</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))}

            <div className="mt-auto rounded-btn border border-border bg-background/60 p-3">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <i className="ri-shield-check-line text-sm leading-none" aria-hidden />
                {t("Доступы")}
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
                {t("AI Prompts настраивает администратор. Продавцы включают готовые правила в поле ответа и проверяют текст перед отправкой.")}
              </p>
            </div>
          </div>

          {/* ── панель AI Prompts ── */}
          <div className="min-w-0 p-3.5 md:p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-2.5">
                <span className="grid size-8 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
                  <i className="ri-question-answer-line text-base leading-none" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-base font-semibold leading-snug">AI Prompts</h3>
                  <p className="mt-1 max-w-xl text-[12px] leading-relaxed text-muted-foreground">
                    {t("Используйте AI Prompts, чтобы настраивать и улучшать формулировки ответов.")}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => flash(t("Демо-режим: подробная справка по AI Prompts в макете недоступна"))}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap text-[12px] font-semibold text-primary transition hover:opacity-80"
              >
                <i className="ri-book-open-line text-sm leading-none" aria-hidden />
                {t("Узнать больше")}
              </button>
            </div>

            <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1 lg:hidden">
              {aiNav.flatMap((g) => g.items).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onNav(it.id)}
                  className={cn(
                    "inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
                    it.id === "ai-prompts" ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  <i className={it.icon} aria-hidden />
                  {it.label}
                </button>
              ))}
            </div>

            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <button
                type="button"
                onClick={openAdd}
                className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
              >
                <Plus className="size-4" aria-hidden />
                {t("Добавить AI-промпт")}
              </button>
              <button
                type="button"
                onClick={() => onNav("ai-assist")}
                className="inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-3.5 text-[12px] font-semibold text-muted-foreground transition hover:text-foreground"
              >
                <i className="ri-sparkling-line" aria-hidden />
                {t("Настройки AI Assist")}
              </button>
            </div>

            <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_17rem]">
              <div className="flex min-w-0 flex-col gap-2">
                {items.map((p) => (
                  <div
                    key={p.id}
                    className={cn(
                      "flex items-start justify-between gap-3 rounded-btn border p-3.5 transition",
                      p.enabled ? "border-border bg-background/60" : "border-border/70 bg-background/25",
                    )}
                  >
                    <div className="flex min-w-0 items-start gap-2.5">
                      <span
                        className={cn(
                          "grid size-8 flex-none place-items-center rounded-btn text-[15px] leading-none",
                          p.enabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
                        )}
                        aria-hidden
                      >
                        <i className={p.icon} />
                      </span>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className={cn("text-[13px] font-semibold leading-snug", !p.enabled && "text-muted-foreground")}>{p.title}</p>
                          {p.builtin ? (
                            <span className="rounded-full border border-border px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                              {t("Встроенный")}
                            </span>
                          ) : (
                            <span className="rounded-full bg-primary/10 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-primary">
                              {t("Свой")}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">{p.description}</p>
                        {!p.builtin ? (
                          <div className="mt-1.5 flex items-center gap-2.5">
                            <button
                              type="button"
                              onClick={() => openEdit(p)}
                              className="inline-flex cursor-pointer items-center gap-1 text-[10.5px] font-semibold text-muted-foreground transition hover:text-foreground"
                            >
                              <Pencil className="size-3" aria-hidden />
                              {t("Изменить")}
                            </button>
                            <button
                              type="button"
                              onClick={() => remove(p)}
                              className="inline-flex cursor-pointer items-center gap-1 text-[10.5px] font-semibold text-muted-foreground transition hover:text-red-500"
                            >
                              <Trash2 className="size-3" aria-hidden />
                              {t("Удалить")}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <button
                      type="button"
                      role="switch"
                      aria-checked={p.enabled}
                      aria-label={t("Промпт {title}", { title: p.title })}
                      onClick={() => toggle(p.id)}
                      className={cn(
                        "relative mt-0.5 h-6 w-11 flex-none cursor-pointer rounded-full border transition",
                        p.enabled ? "border-primary bg-primary" : "border-border bg-foreground/15",
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn("absolute top-0.5 size-[18px] rounded-full bg-white transition-all", p.enabled ? "left-[22px]" : "left-0.5")}
                      />
                    </button>
                  </div>
                ))}

                <div className="mt-1 flex flex-wrap items-center justify-between gap-2 rounded-btn border border-border bg-background/40 px-3 py-2.5 text-[11px] text-muted-foreground">
                  <span>
                    {t("Включено {n} из {total} · правила применяются к черновику ответа", { n: enabled.length, total: items.length })}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setItems((prev) => prev.map((p) => ({ ...p, enabled: true })));
                      flash(t("Включили все AI Prompts — ассистент будет применять их к каждому черновику"));
                    }}
                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap font-semibold text-primary transition hover:opacity-80"
                  >
                    <i className="ri-toggle-line text-[13px] leading-none" aria-hidden />
                    {t("Включить все")}
                  </button>
                </div>
              </div>

              {/* ── превью в диалоге ── */}
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("Как это в диалоге")}</p>
                <div className="mt-2 overflow-hidden rounded-btn border border-border bg-background/60">
                  <div className="flex items-center gap-2 border-b border-border px-2.5 py-2">
                    <span className="grid size-6 flex-none place-items-center rounded-full bg-primary/15 text-[10px] font-bold text-primary" aria-hidden>
                      {t("ОС")}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[11px] font-semibold">{t("Ольга Соколова")}</span>
                    <i className="ri-whatsapp-line text-[13px] leading-none text-primary" aria-hidden />
                  </div>

                  <div className="p-2.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Черновик ответа")}</p>
                    <p className="mt-1 rounded-btn rounded-ss-sm bg-muted px-2.5 py-2 text-[11px] leading-relaxed text-foreground">{draft}</p>

                    <div className="mt-2 flex items-center gap-2.5 border-t border-border/70 pt-2 text-[13px] text-muted-foreground" aria-hidden>
                      <i className="ri-attachment-2" />
                      <i className="ri-emotion-happy-line" />
                      <i className="ri-text" />
                      <i className="ri-magic-line" />
                      <span className="ms-auto inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[9.5px] font-bold text-primary-foreground">
                        <i className="ri-question-answer-line text-[10px] leading-none" />
                        AI Prompts
                      </span>
                    </div>

                    <div className="mt-2 rounded-btn border border-primary/30 bg-primary/5 p-2">
                      <p className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-primary">
                        <i className="ri-question-answer-line text-[10px] leading-none" aria-hidden />
                        {t("Активные промпты")}
                      </p>
                      {enabled.length === 0 ? (
                        <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">
                          {t("Все промпты выключены — ассистент не будет править текст.")}
                        </p>
                      ) : (
                        <ul className="mt-1.5 flex flex-col gap-1">
                          {enabled.map((p) => (
                            <li key={p.id}>
                              <button
                                type="button"
                                onClick={() => toggle(p.id)}
                                className="flex w-full cursor-pointer items-center gap-1.5 text-start text-[10.5px] font-medium text-foreground transition hover:text-primary"
                              >
                                <Check className="size-3 flex-none text-primary" strokeWidth={3} aria-hidden />
                                <span className="min-w-0 flex-1 truncate">{p.title}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        <button
                          type="button"
                          onClick={run}
                          className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-btn bg-primary px-2 py-1 text-[10px] font-semibold text-primary-foreground transition hover:brightness-110"
                        >
                          <i className="ri-sparkling-line text-[10px] leading-none" aria-hidden />
                          {t("Применить")}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDraft(sampleDraft);
                            flash(t("Черновик вернулся к исходному виду"));
                          }}
                          className="inline-flex cursor-pointer items-center whitespace-nowrap rounded-btn border border-border px-1.5 py-1 text-[10px] font-semibold text-muted-foreground transition hover:text-foreground"
                        >
                          {t("Сбросить")}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
                  {t("Используйте AI Prompts, чтобы менять тон, исправлять грамматику, переводить или упрощать ответы. Выберите готовые или")}{" "}
                  <button type="button" onClick={openAdd} className="cursor-pointer font-semibold text-primary hover:underline">
                    {t("создайте свой промпт")}
                  </button>
                  .
                </p>
                <p className="mt-1.5 flex items-start gap-1.5 text-[10.5px] leading-relaxed text-muted-foreground">
                  <i className="ri-information-line mt-px text-[12px] leading-none text-primary" aria-hidden />
                  {promptsHint}
                </p>
              </div>
            </div>

            <p className="mt-3 text-[11px] text-muted-foreground">
              {t("Демо-режим: промпты меняются только в макете — настоящие ответы клиентам не отправляются.")}
            </p>
          </div>
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}

        {/* ── окно создания промпта ── */}
        {addOpen ? (
          <div className="absolute inset-0 z-[60] flex items-end justify-center bg-black/65 p-3 backdrop-blur-sm sm:items-center md:p-6">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                save();
              }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="prompt-dialog-title"
              className="relative max-h-full w-full max-w-lg overflow-y-auto rounded-card border border-border bg-card p-5 shadow-lift md:p-6"
            >
              <button
                type="button"
                onClick={() => setAddOpen(false)}
                aria-label={t("Закрыть окно промпта")}
                className="absolute end-3 top-3 grid size-8 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" aria-hidden />
              </button>

              <div className="flex items-start gap-3 pe-8">
                <span className="grid size-11 flex-none place-items-center rounded-btn bg-primary/10 text-2xl leading-none text-primary" aria-hidden>
                  <i className="ri-question-answer-line" />
                </span>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    {editId ? t("Свой промпт") : t("Новый AI-промпт")}
                  </p>
                  <h3 id="prompt-dialog-title" className="mt-1 text-lg font-semibold leading-snug">
                    {editId ? t("Изменить промпт") : t("Добавить AI-промпт")}
                  </h3>
                </div>
              </div>

              <p className="mt-3 text-[11.5px] leading-relaxed text-muted-foreground">
                {t("Ассистент применит правило к черновику ответа. Продавец увидит результат и сможет поправить его перед отправкой.")}
              </p>

              <div className="mt-3 flex gap-1 overflow-x-auto rounded-btn border border-border bg-background/60 p-1">
                {promptKinds.map((k) => (
                  <button
                    key={k.id}
                    type="button"
                    onClick={() => {
                      setForm((f) => ({
                        ...f,
                        kind: k.id,
                        icon: k.icon,
                        name: f.name.trim() ? f.name : k.id === "custom" ? "" : k.label,
                        description: f.description.trim() ? f.description : k.id === "custom" ? "" : k.hint,
                      }));
                      setError("");
                    }}
                    className={cn(
                      "inline-flex flex-none cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn px-2.5 py-1.5 text-[11.5px] font-semibold transition",
                      form.kind === k.id ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <i className={cn(k.icon, "text-[13px] leading-none")} aria-hidden />
                    {k.label}
                  </button>
                ))}
              </div>

              <p className="mt-2 text-[10.5px] text-muted-foreground">
                {promptKinds.find((k) => k.id === form.kind)?.hint ?? ""}
              </p>

              <div className="mt-3 flex flex-col gap-3">
                <label className="flex min-w-0 flex-col gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Название")}</span>
                  <input
                    value={form.name}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, name: e.target.value.slice(0, 60) }));
                      setError("");
                    }}
                    placeholder={t("Например, Сократить ответ")}
                    autoComplete="off"
                    className={cn(INPUT, "h-9")}
                  />
                </label>

                <label className="flex min-w-0 flex-col gap-1.5">
                  <span className="flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("Что видит продавец")}
                    <span className="normal-case tracking-normal tabular-nums">{form.description.length}/140</span>
                  </span>
                  <input
                    value={form.description}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, description: e.target.value.slice(0, 140) }));
                      setError("");
                    }}
                    placeholder={t("Сделать ответ короче, оставив только суть")}
                    autoComplete="off"
                    className={cn(INPUT, "h-9")}
                  />
                </label>

                <label className="flex min-w-0 flex-col gap-1.5">
                  <span className="flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("Инструкция для ассистента")}
                    <span className="normal-case tracking-normal tabular-nums">{form.instruction.length}/400</span>
                  </span>
                  <textarea
                    value={form.instruction}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, instruction: e.target.value.slice(0, 400) }));
                      setError("");
                    }}
                    rows={4}
                    placeholder={t("Сократи ответ до трёх предложений. Оставь цены, сроки и ссылки без изменений.")}
                    className={cn(INPUT, "h-auto min-h-[96px] resize-y py-2.5 leading-relaxed")}
                  />
                </label>

                <div className="flex min-w-0 flex-col gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("Иконка")}</span>
                  <IconChoice value={form.icon} onChange={(icon) => setForm((f) => ({ ...f, icon }))} />
                </div>
              </div>

              {error ? <p className="mt-2 text-[11px] text-red-500">{error}</p> : null}

              <p className="mt-3 flex items-start gap-2 rounded-btn border border-border bg-background/60 px-3 py-2.5 text-[11px] leading-relaxed text-muted-foreground">
                <i className="ri-information-line mt-0.5 text-[13px] leading-none text-primary" aria-hidden />
                {t("Свой промпт можно выключить в любой момент — он останется в списке, но не будет применяться к ответам.")}
              </p>

              <div className="mt-4 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => setAddOpen(false)}
                  className="inline-flex h-10 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn border border-border px-4 text-[13px] font-semibold text-muted-foreground transition hover:text-foreground"
                >
                  {t("Отмена")}
                </button>
                <button
                  type="submit"
                  disabled={!canSave}
                  className="inline-flex h-10 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-btn bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <i className={editId ? "ri-check-line" : "ri-add-line"} aria-hidden />
                  {editId ? t("Сохранить промпт") : t("Создать промпт")}
                </button>
              </div>
            </form>
          </div>
        ) : null}
      </div>
    </Band>
  );
}

export default PromptsDemo;